/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { createSelector } from "@reduxjs/toolkit";
import { AppState } from "../../";
import { isJwtExpired } from "../../../utils/helpers";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { IGeoRegion, IProfile } from "../admin/types";

// These are "input selectors" meant to just extract and return values
export const isLoggedInSelector = createSelector(
  [(state: AppState) => state.authentication.isLoggedIn],
  (isLoggedIn) => {
    return !!isLoggedIn;
  }
);

export const idTokenSelector = createSelector(
  [(state: AppState) => state.authentication.idToken as string],
  (idToken) => {
    if (idToken) {
      return idToken;
    }
    return null;
  }
);

export const usernameSelector = createSelector(
  [(state: AppState) => state.authentication.username as string],
  (username) => {
    if (username) {
      return username;
    }
    return '';
  }
);

export const selectProfilePermissionsSelector = createSelector(
  [(state: AppState) => state.authentication.profile as IProfile],
  (profile) => {
    if (profile) {
      return new ProfilePermissions(profile);
    }
    return null;
  }
);

export const siteGroupSelector = createSelector(
  [(state: AppState) => state.authentication.profile.geoRegions as IGeoRegion[]],
  (regions) => {
    if (regions && regions.length) {
      return regions;
    }
    return [];
  }
);

// This is an "output selector" which showcases how powerful selectors can be. You can basically build any output by looking at specific properties from your various slices
export const dummyComplexSelector = createSelector(
    isLoggedInSelector,
    idTokenSelector,
    (isLoggedIn, idToken) => {
        if (isLoggedIn && idToken && !isJwtExpired(idToken)) {
            return "The user is logged in and has a valid token";
        }
        return "The user is not logged in!";
    }
)

export const authenticationSelector = ((state: AppState) => state.authentication);
// Here is an example of something to avoid when implementing selectors. Creating an output selector when it is supposed to be an input selector
// export const outputLoggedInSelector = createSelector(
//     authenticationSelector,
//     (auth) => auth.isLoggedIn
// );
